import type { ComponentType } from "react";

/**
 * Code-split seam for the Claude Design Workshop detail page.
 *
 * `claude-design-workshop-detail.tsx` chains in the entire
 * `components/sections/design-workshop/` subtree (~5.5K LOC). It used to be
 * statically imported by `event-detail.tsx`, which is itself eagerly imported
 * by App.tsx (an SSR-hydration requirement, see App.tsx) — so every visitor
 * downloaded the design-workshop page inside the entry chunk.
 *
 * The dispatch to the design page happens at runtime inside EventDetail
 * (`event.seriesSlug === "claude-for-design-workshop"`), so a plain
 * React.lazy would make renderToString emit an empty fallback for that route
 * (no SSR content) and would hydration-mismatch on direct hits. Instead:
 *
 * - SERVER: `routes-ssr.tsx` (server-only module) imports the page eagerly
 *   and registers it here at module scope, so renderToString renders the
 *   full page synchronously. Nothing changes for SSR.
 * - CLIENT: the component is only reachable via `preloadDesignWorkshopDetail`
 *   (dynamic import → own chunk). `entry-client.tsx` awaits the preload
 *   BEFORE `hydrateRoot` when the dehydrated query state contains a
 *   design-workshop event, so the client's first render produces the same
 *   tree the server sent — no Suspense, no mismatch, no flash.
 * - Client-side navigation: the gate inside event-detail.tsx loads the chunk
 *   on demand and shows the standard loading skeleton meanwhile.
 */

let DesignWorkshopDetail: ComponentType | null = null;

export function getDesignWorkshopDetail(): ComponentType | null {
  return DesignWorkshopDetail;
}

/** Called by routes-ssr.tsx (server bundle only) with the eager import. */
export function setDesignWorkshopDetail(component: ComponentType): void {
  DesignWorkshopDetail = component;
}

let pending: Promise<ComponentType> | null = null;

export function preloadDesignWorkshopDetail(): Promise<ComponentType> {
  if (DesignWorkshopDetail) return Promise.resolve(DesignWorkshopDetail);
  if (!pending) {
    pending = import("@/pages/claude-design-workshop-detail").then((m) => {
      DesignWorkshopDetail = m.default;
      return m.default;
    });
    // Allow a retry if the chunk fails to load (e.g. flaky network).
    pending.catch(() => {
      pending = null;
    });
  }
  return pending;
}
